import type { AnnotationEntry } from '@geosoft/viewer-sdk';
import { haversineMeters, formatDistanceMeters } from '@/components/plugins/geo';

export interface MeasurementSummary {
  readonly distanceLabel: string;
  readonly surfaceLabel: string;
  readonly angleLabel: string;
  readonly heightLabel: string;
}

export function summarizeMeasurements(
  points: ReadonlyArray<readonly [number, number]>,
  annotations: ReadonlyArray<AnnotationEntry>,
): MeasurementSummary {
  return {
    distanceLabel: measureDistance(points),
    surfaceLabel: measureSurface(annotations),
    angleLabel: measureAngle(points),
    heightLabel: 'height pending terrain',
  };
}

export function measureDistance(points: ReadonlyArray<readonly [number, number]>): string {
  if (points.length < 2) return 'click two or more points on the map';
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += haversineMeters(points[i - 1], points[i]);
  }
  return formatDistanceMeters(total);
}

export function measureSurface(annotations: ReadonlyArray<AnnotationEntry>): string {
  const polygon = annotations.find((annotation) => annotation.geometry.type === 'Polygon');
  if (!polygon || polygon.geometry.type !== 'Polygon') return 'draw or select a polygon';

  const ring = polygon.geometry.coordinates;
  if (!Array.isArray(ring) || !Array.isArray(ring[0])) return 'draw or select a polygon';
  const area = approximatePolygonAreaSquareMeters(ring[0] as Array<[number, number]>);
  if (!Number.isFinite(area) || area <= 0) return 'draw or select a polygon';
  return area >= 1_000_000 ? `${(area / 1_000_000).toFixed(2)} km2` : `${Math.round(area)} m2`;
}

export function measureAngle(points: ReadonlyArray<readonly [number, number]>): string {
  if (points.length < 3) return 'select three map points';
  const [a, b, c] = points.slice(-3);
  const ab = [a[0] - b[0], a[1] - b[1]];
  const cb = [c[0] - b[0], c[1] - b[1]];
  const dot = ab[0] * cb[0] + ab[1] * cb[1];
  const len = Math.hypot(ab[0], ab[1]) * Math.hypot(cb[0], cb[1]);
  if (len === 0) return 'n/a';
  const radians = Math.acos(Math.max(-1, Math.min(1, dot / len)));
  return `${((radians * 180) / Math.PI).toFixed(1)} deg`;
}

function approximatePolygonAreaSquareMeters(ring: Array<[number, number]>): number {
  if (ring.length < 4) return 0;
  const lat0 = ring.reduce((sum, [, lat]) => sum + lat, 0) / ring.length;
  const metersPerDegreeLat = 111_320;
  const metersPerDegreeLon = 111_320 * Math.cos((lat0 * Math.PI) / 180);
  const coords = ring.map(([lon, lat]) => [lon * metersPerDegreeLon, lat * metersPerDegreeLat]);

  let area = 0;
  for (let i = 0; i < coords.length - 1; i += 1) {
    const [x1, y1] = coords[i];
    const [x2, y2] = coords[i + 1];
    area += x1 * y2 - x2 * y1;
  }
  return Math.abs(area / 2);
}
